import { useLocation } from 'react-router-dom';
import { NavLink } from '@/components/NavLink';
import { useApp } from '@/contexts/AppContext';
import { useBrand } from '@/hooks/useBrand';
import { BrandedLogo } from '@/components/brand/BrandedLogo';
import { useAuth } from '@/contexts/AuthContext';
import { ScopeBreadcrumb } from './ScopeBreadcrumb';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  Building2,
  TrendingUp,
  Settings,
  LogOut,
  Zap,
  SlidersHorizontal,
  FileText,
  CreditCard,
  Package,
  Radio,
  Monitor,
  ClipboardList,
  ClipboardCheck,
  Lightbulb,
  HeadsetIcon,
} from 'lucide-react';
import { useCanSeeBilling } from '@/hooks/useBillingPreview';
import { ThemeToggle } from '@/components/ThemeToggle';
import type { NavigationScope } from '@/contexts/AppContext';

/** Exported for tests. Tier precedence: predict > alert > monitor. */
export function useOrgHighestTier(plants: { tier?: string }[]): 'monitor' | 'alert' | 'predict' {
  if (plants.some((p) => p.tier === 'predict')) return 'predict';
  if (plants.some((p) => p.tier === 'alert')) return 'alert';
  return 'monitor';
}

/** Billing statuses that hide Alert/Predict nav (Notifications, Reports). */
const BILLING_STATUSES_HIDING_ALERT_NAV = ['suspended', 'canceled', 'none'] as const;

type NavItemDef = {
  title: string;
  /** Base key — used to compute scope-driven URL */
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  /** If true, this route is always fleet-level (scope doesn't change its URL) */
  fleetOnly?: boolean;
  /** If true, clicking this item resets scope to fleet */
  resetsToFleet?: boolean;
};

/** Compute the actual URL for a nav item based on scope. */
function getScopedUrl(key: string, scope: NavigationScope): string {
  if (scope.type === 'property' && scope.propertyId) {
    switch (key) {
      case 'dashboard': return `/app/property/${scope.propertyId}`;
      case 'analytics': return `/app/analytics?property=${scope.propertyId}`;
      case 'automations': return `/app/automations?property=${scope.propertyId}`;
      case 'control': return `/app/control?property=${scope.propertyId}`;
      case 'reports': return `/app/reports?property=${scope.propertyId}`;
      case 'settings': return `/app/settings?property=${scope.propertyId}`;
    }
  }
  // Fleet scope or fleet-only items
  switch (key) {
    case 'dashboard': return '/app/fleet';
    case 'fleet': return '/app/fleet';
    case 'analytics': return '/app/analytics';
    case 'issues': return '/app/issues';
    case 'visits': return '/app/visits';
    case 'automations': return '/app/automations';
    case 'control': return '/app/control';
    case 'gateways': return '/app/gateways';
    case 'opportunities': return '/app/opportunities';
    case 'reports': return '/app/reports';
    case 'billing': return '/app/billing';
    case 'settings': return '/app/settings';
    default: return '/app';
  }
}

/** Check if a nav item is active based on current location and scope. */
function isNavActive(key: string, scope: NavigationScope, pathname: string, search: string): boolean {
  const url = getScopedUrl(key, scope);

  // For items with query params, match pathname + query param
  if (url.includes('?')) {
    const [path, qs] = url.split('?');
    return pathname === path && search.includes(qs);
  }

  // Special: "fleet" matches /app/fleet
  if (key === 'fleet') return pathname === '/app/fleet';

  // Special: "dashboard" in property scope matches /app/property/:id
  if (key === 'dashboard' && scope.type === 'property' && scope.propertyId) {
    return pathname === `/app/property/${scope.propertyId}`;
  }

  // Special: "dashboard" in fleet scope matches /app/fleet
  if (key === 'dashboard' && scope.type === 'fleet') {
    return pathname === '/app/fleet';
  }

  // Special: pages with sub-paths
  if (key === 'issues') return pathname.startsWith('/app/issues');
  if (key === 'visits') return pathname.startsWith('/app/visits');
  if (key === 'automations') return pathname.startsWith('/app/automations');
  if (key === 'opportunities') return pathname === '/app/opportunities';
  if (key === 'gateways') return pathname.startsWith('/app/gateways');

  return pathname === url.split('?')[0];
}

/** OBSERVE: Dashboard, Fleet, Analytics, Opportunities */
const NAV_OBSERVE: NavItemDef[] = [
  { title: 'Dashboard', key: 'dashboard', icon: LayoutDashboard },
  { title: 'Fleet', key: 'fleet', icon: Building2, fleetOnly: true, resetsToFleet: true },
  { title: 'Analytics', key: 'analytics', icon: TrendingUp },
  { title: 'Opportunities', key: 'opportunities', icon: Lightbulb, fleetOnly: true },
];

/** RESPOND: Issues, Visits, Automations, Control */
const NAV_RESPOND: NavItemDef[] = [
  { title: 'Issues', key: 'issues', icon: ClipboardList, fleetOnly: true },
  { title: 'Visits', key: 'visits', icon: ClipboardCheck, fleetOnly: true },
  { title: 'Automations', key: 'automations', icon: Zap },
  { title: 'Control', key: 'control', icon: SlidersHorizontal },
];

/** MANAGE: Gateways, Reports, Billing, Settings */
const NAV_MANAGE_BASE: NavItemDef[] = [
  { title: 'Reports', key: 'reports', icon: FileText },
  { title: 'Billing', key: 'billing', icon: CreditCard, fleetOnly: true },
  { title: 'Settings', key: 'settings', icon: Settings },
];

export function AppSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { properties, plants, isInstaller, isHomeowner, organization, userProfile, navigationScope, setFleetScope } = useApp();
  const { companyName, poweredByWt } = useBrand();
  const { signOut } = useAuth();
  const canSeeBilling = useCanSeeBilling();
  const orgHighestTier = useOrgHighestTier(plants);
  const collapsed = state === 'collapsed';
  const billingHidesAlertNav = BILLING_STATUSES_HIDING_ALERT_NAV.includes((organization?.billing_status ?? 'none') as typeof BILLING_STATUSES_HIDING_ALERT_NAV[number]);
  const showAutomationNav = orgHighestTier !== 'monitor' && !billingHidesAlertNav;
  const gatewayAccess = (organization?.feature_flags as Record<string, boolean> | null)?.gateway_access === true;

  const scope = navigationScope;

  /** Installer: grouped sections. */
  const installerSections: { label?: string; items: NavItemDef[] }[] = isInstaller
    ? [
        { label: 'OBSERVE', items: NAV_OBSERVE },
        { label: 'RESPOND', items: showAutomationNav ? NAV_RESPOND : [] },
        {
          label: 'MANAGE',
          items: [
            ...(gatewayAccess ? [{ title: 'Gateways', key: 'gateways', icon: Radio, fleetOnly: true } as NavItemDef] : []),
            ...NAV_MANAGE_BASE.filter((item) => {
              if (item.key === 'billing') return canSeeBilling;
              if (item.key === 'reports') return showAutomationNav;
              return true;
            }),
          ],
        },
      ]
    : [];

  /** Homeowner: flat nav (permanent property scope). */
  const homeownerNav: NavItemDef[] = isInstaller
    ? []
    : [
        { title: 'Dashboard', key: 'dashboard', icon: LayoutDashboard },
        { title: 'Analytics', key: 'analytics', icon: TrendingUp },
        { title: 'Settings', key: 'settings', icon: Settings },
      ];

  // Homeowner scope: always property scope for first property
  const effectiveScope: NavigationScope = isHomeowner && properties.length > 0
    ? { type: 'property', propertyId: properties[0].id, propertyName: properties[0].name }
    : scope;

  const handleSignOut = async () => {
    await signOut();
  };

  const renderNavItem = (item: NavItemDef) => {
    const itemScope = item.fleetOnly ? { type: 'fleet' as const } : effectiveScope;
    const url = getScopedUrl(item.key, itemScope);
    const active = isNavActive(item.key, itemScope, location.pathname, location.search);

    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
          <NavLink
            to={url}
            className="flex items-center gap-3"
            activeClassName="bg-wt-bg-secondary text-foreground"
            onClick={item.resetsToFleet ? () => setFleetScope() : undefined}
          >
            <item.icon className="h-4 w-4" />
            {!collapsed && <span>{item.title}</span>}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="!bg-wt-bg-primary text-foreground">
      <SidebarHeader className="border-b border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <BrandedLogo size="h-10 w-10" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-sidebar-foreground">{companyName}</span>
              {poweredByWt ? (
                <span className="text-xs text-muted-foreground">Powered by Watt Tower</span>
              ) : (
                <span className="text-xs text-muted-foreground">Energy Intelligence</span>
              )}
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Interactive scope breadcrumb */}
        {!collapsed && (
          <div className="px-3 py-2 border-b border-border">
            <ScopeBreadcrumb />
          </div>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {isInstaller ? (
                installerSections.map((section, sectionIndex) => (
                  <div key={section.label ?? 'nav'} className={sectionIndex > 0 ? 'mt-4' : undefined}>
                    {!collapsed && section.label && (
                      <p className="px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {section.label}
                      </p>
                    )}
                    {section.items.map(renderNavItem)}
                  </div>
                ))
              ) : (
                homeownerNav.map(renderNavItem)
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin-only nav */}
        {userProfile?.role === 'admin' && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {userProfile?.is_super_admin && (
                  <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === '/app/admin'} tooltip="Ops Console">
                      <NavLink to="/app/admin" className="flex items-center gap-3" activeClassName="bg-wt-bg-secondary text-foreground">
                        <Monitor className="h-4 w-4" />
                        {!collapsed && <span>Ops Console</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === '/app/admin/support'} tooltip="Support">
                      <NavLink to="/app/admin/support" className="flex items-center gap-3" activeClassName="bg-wt-bg-secondary text-foreground">
                        <HeadsetIcon className="h-4 w-4" />
                        {!collapsed && <span>Support</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  </>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === '/app/admin/firmware'} tooltip="Firmware">
                    <NavLink to="/app/admin/firmware" className="flex items-center gap-3" activeClassName="bg-wt-bg-secondary text-foreground">
                      <Package className="h-4 w-4" />
                      {!collapsed && <span>Firmware</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center justify-between px-2">
              <SidebarMenuButton
                onClick={handleSignOut}
                tooltip="Sign Out"
                className="text-muted-foreground hover:text-foreground flex-1"
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span>Sign Out</span>}
              </SidebarMenuButton>
              {!collapsed && <ThemeToggle />}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
